import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle2,
  Phone,
  Mail,
  Minus,
  Plus,
  ShieldCheck,
  Droplets,
  Repeat,
  Info,
  Loader2,
  MapPin,
  Building2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRICE_PER_WINDOW = 10;
const TOTAL_STEPS = 8;

const accessibilityOptions = [
  { value: 'gut', title: 'Ja, gut erreichbar', desc: 'Die Fenster sind von innen und außen gut erreichbar.' },
  { value: 'teilweise', title: 'Teilweise schwer erreichbar', desc: 'Einige Fenster sind nur eingeschränkt zugänglich.' },
  { value: 'schwer', title: 'Nein, schwer zugänglich', desc: 'Einige Fenster sind schwer zugänglich.' },
];

const floorOptions = [
  { value: 'eg', title: 'Erdgeschoss' },
  { value: 'og', title: 'Obergeschoss' },
  { value: 'hoeher', title: 'Höheres Stockwerk' },
  { value: 'verschiedene', title: 'Verschiedene Stockwerke' },
];

const dirtOptions = [
  { value: 'normal', title: 'Normal verschmutzt' },
  { value: 'staerker', title: 'Stärker verschmutzt' },
  { value: 'sehr', title: 'Sehr stark verschmutzt' },
  { value: 'unsicher', title: 'Ich bin mir nicht sicher' },
];

const intervalOptions = [
  { value: 'einmalig', title: 'Einmalige Reinigung', desc: 'Für eine einzelne Fensterreinigung nach Bedarf.' },
  { value: '2', title: '2× pro Jahr', desc: 'Ideal z. B. für Frühjahr und Herbst.' },
  { value: '4', title: '4× pro Jahr', desc: 'Für regelmäßig saubere Fenster übers Jahr.' },
  { value: '8', title: '8× pro Jahr', desc: 'Für besonders häufige Fensterreinigung.' },
  { value: 'individuell', title: 'Individuelles Intervall', desc: 'z. B. monatlich oder ein anderer Rhythmus.' },
];

const labelMaps = {
  accessibility: Object.fromEntries(accessibilityOptions.map((o) => [o.value, o.title])),
  floor: Object.fromEntries(floorOptions.map((o) => [o.value, o.title])),
  dirt: Object.fromEntries(dirtOptions.map((o) => [o.value, o.title])),
  interval: Object.fromEntries(intervalOptions.map((o) => [o.value, o.title])),
};

const initialForm = {
  firstName: '',
  lastName: '',
  street: '',
  houseNumber: '',
  zip: '',
  city: 'Oldenburg',
  phone: '',
  email: '',
  windowCount: 1,
  accessibility: '',
  accessibilityNote: '',
  floor: '',
  floorNote: '',
  dirt: '',
  extras: '',
  interval: '',
  intervalCustom: '',
};

const inputCls =
  'w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-[#0052CC] focus:ring-2 focus:ring-[#E6F0FF] outline-none transition';

const SectionBadge = ({ children }) => (
  <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
    {children}
  </span>
);

const Field = ({ label, error, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const OptionCard = ({ active, onClick, title, desc, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-start gap-3 ${
      active ? 'border-[#0052CC] bg-[#E6F0FF]' : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <span
      className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        active ? 'border-[#0052CC]' : 'border-slate-300'
      }`}
    >
      {active && <span className="w-2.5 h-2.5 rounded-full bg-[#0052CC]" />}
    </span>
    <span>
      <span className="block font-medium text-slate-900">{title}</span>
      {desc && <span className="block text-sm text-slate-500 mt-0.5">{desc}</span>}
    </span>
  </button>
);

const NoticeBox = ({ children }) => (
  <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800">
    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
    <p>{children}</p>
  </div>
);

const WindowConfigurator = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [confirmData, setConfirmData] = useState(false);
  const [confirmPrivacy, setConfirmPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const topRef = useRef(null);

  const price = Math.max(0, Number(form.windowCount) || 0) * PRICE_PER_WINDOW;

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const scrollTop = () => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.firstName.trim()) e.firstName = 'Bitte Vornamen angeben';
      if (!form.lastName.trim()) e.lastName = 'Bitte Nachnamen angeben';
      if (!form.street.trim()) e.street = 'Bitte Straße angeben';
      if (!form.houseNumber.trim()) e.houseNumber = 'Bitte Hausnummer angeben';
      if (!form.zip.trim()) e.zip = 'Bitte PLZ angeben';
      if (!form.city.trim()) e.city = 'Bitte Ort angeben';
      if (!form.phone.trim()) e.phone = 'Bitte Telefonnummer angeben';
      if (!form.email.trim()) e.email = 'Bitte E-Mail angeben';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Bitte gültige E-Mail angeben';
    }
    if (s === 2) {
      if (!form.windowCount || Number(form.windowCount) < 1) e.windowCount = 'Bitte mindestens 1 Fenster angeben';
    }
    if (s === 4) {
      if (!form.accessibility) e.accessibility = 'Bitte eine Option wählen';
    }
    if (s === 5) {
      if (!form.floor) e.floor = 'Bitte ein Stockwerk wählen';
    }
    if (s === 6) {
      if (!form.dirt) e.dirt = 'Bitte eine Option wählen';
    }
    if (s === 8) {
      if (!form.interval) e.interval = 'Bitte ein Intervall wählen';
      else if (form.interval === 'individuell' && !form.intervalCustom.trim())
        e.intervalCustom = 'Bitte Ihr gewünschtes Intervall angeben';
    }
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setStep((s) => s + 1);
    scrollTop();
  };

  const back = () => {
    setStep((s) => s - 1);
    scrollTop();
  };

  const submit = async () => {
    const e = {};
    if (!confirmData) e.confirmData = true;
    if (!confirmPrivacy) e.confirmPrivacy = true;
    if (Object.keys(e).length) {
      setErrors((prev) => ({ ...prev, ...e }));
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/window-cleaning`, {
        ...form,
        windowCount: Number(form.windowCount),
        priceEstimate: price,
      });
      setSubmitted(true);
      scrollTop();
    } catch (err) {
      setSubmitError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div ref={topRef} className="max-w-2xl mx-auto text-center bg-white rounded-3xl border border-slate-100 shadow-sm p-10" data-testid="window-success">
        <div className="w-16 h-16 bg-[#00C2B8] rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-9 h-9 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Vielen Dank für Ihre Anfrage!
        </h3>
        <p className="text-slate-600 mb-8 leading-relaxed">
          Wir haben Ihre Anfrage zur Fensterreinigung erhalten und melden uns schnellstmöglich bei Ihnen. Wir prüfen
          Ihre Angaben und melden uns anschließend mit einer Rückmeldung bzw. einem konkreten Angebot bei Ihnen.
        </p>
        <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid="window-success-home">
          Zur Startseite <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    );
  }

  const isReview = step > TOTAL_STEPS;
  const progress = Math.min(step, TOTAL_STEPS);

  return (
    <div ref={topRef} className="max-w-3xl mx-auto">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-500" data-testid="window-progress-label">
            {isReview ? 'Zusammenfassung' : `Schritt ${progress} von ${TOTAL_STEPS}`}
          </span>
          <span className="text-sm font-semibold text-[#0052CC]" data-testid="window-price-badge">
            <span className="font-bold uppercase text-xs">ab</span> {price} €
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#0052CC] rounded-full transition-all duration-300"
            style={{ width: `${(progress / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8">
        {/* STEP 1 */}
        {step === 1 && (
          <div data-testid="window-step-1">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wo dürfen wir Ihre Fenster reinigen?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Ihre Kontaktdaten und die Adresse des Einsatzortes.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Vorname" required error={errors.firstName}>
                <input className={inputCls} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} data-testid="window-firstName" />
              </Field>
              <Field label="Nachname" required error={errors.lastName}>
                <input className={inputCls} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} data-testid="window-lastName" />
              </Field>
              <Field label="Straße" required error={errors.street}>
                <input className={inputCls} value={form.street} onChange={(e) => set('street', e.target.value)} data-testid="window-street" />
              </Field>
              <Field label="Hausnummer" required error={errors.houseNumber}>
                <input className={inputCls} value={form.houseNumber} onChange={(e) => set('houseNumber', e.target.value)} data-testid="window-houseNumber" />
              </Field>
              <Field label="PLZ" required error={errors.zip}>
                <input className={inputCls} value={form.zip} onChange={(e) => set('zip', e.target.value)} data-testid="window-zip" />
              </Field>
              <Field label="Ort" required error={errors.city}>
                <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="window-city" />
              </Field>
              <Field label="Telefonnummer" required error={errors.phone}>
                <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="window-phone" />
              </Field>
              <Field label="E-Mail-Adresse" required error={errors.email}>
                <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="window-email" />
              </Field>
            </div>
            <p className="text-xs text-slate-400 mt-4">Unser Angebot ist auf Oldenburg und Umgebung ausgerichtet – gerne prüfen wir auch umliegende Orte.</p>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div data-testid="window-step-2">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wie viele Fenster möchten Sie reinigen lassen?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Der Grundpreis beträgt ab 10 € pro Fenster.</p>
            <div className="flex items-center justify-center gap-6 py-4">
              <button
                type="button"
                onClick={() => set('windowCount', Math.max(1, Number(form.windowCount) - 1))}
                className="w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-[#0052CC] hover:text-[#0052CC] transition"
                data-testid="window-count-minus"
                aria-label="Ein Fenster weniger"
              >
                <Minus className="w-5 h-5" />
              </button>
              <input
                type="number"
                min="1"
                value={form.windowCount}
                onChange={(e) => set('windowCount', e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-24 text-center text-3xl font-bold text-slate-900 border-b-2 border-slate-200 focus:border-[#0052CC] outline-none py-2"
                data-testid="window-count-input"
              />
              <button
                type="button"
                onClick={() => set('windowCount', Number(form.windowCount || 0) + 1)}
                className="w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center hover:border-[#0052CC] hover:text-[#0052CC] transition"
                data-testid="window-count-plus"
                aria-label="Ein Fenster mehr"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {errors.windowCount && <p className="text-red-500 text-sm text-center">{errors.windowCount}</p>}
            <div className="mt-6 bg-[#E6F0FF] rounded-xl p-5 text-center">
              <p className="text-slate-600 text-sm">Preisorientierung</p>
              <p className="text-2xl font-bold text-[#0052CC]" data-testid="window-step2-price">
                <span className="text-sm uppercase">ab</span> {price} €
              </p>
              <p className="text-slate-500 text-sm mt-1">{form.windowCount || 0} Fenster × 10 €</p>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div data-testid="window-step-3">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Das ist im Grundpreis enthalten
            </h3>
            <p className="text-slate-500 text-sm mb-6">Diese Leistungen gehören zur Standard-Fensterreinigung.</p>
            <div className="space-y-3">
              {['Glas innen', 'Glas außen', 'Normaler Fensterrahmen'].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-[#E6F0FF] rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-[#0052CC]" />
                  <span className="font-medium text-slate-900">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-5">
              Der äußere Falz bzw. tieferliegende Rahmenbereiche sind nicht automatisch Bestandteil der
              Standardreinigung. Bei Bedarf erstellen wir Ihnen dafür gerne ein individuelles Angebot.
            </p>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div data-testid="window-step-4">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sind die Fenster von innen und außen gut erreichbar?
            </h3>
            <p className="text-slate-500 text-sm mb-6">So können wir den Aufwand besser einschätzen.</p>
            <div className="space-y-3">
              {accessibilityOptions.map((o) => (
                <OptionCard
                  key={o.value}
                  active={form.accessibility === o.value}
                  onClick={() => set('accessibility', o.value)}
                  title={o.title}
                  desc={o.desc}
                  testid={`window-access-${o.value}`}
                />
              ))}
            </div>
            {errors.accessibility && <p className="text-red-500 text-xs mt-2">{errors.accessibility}</p>}
            {(form.accessibility === 'teilweise' || form.accessibility === 'schwer') && (
              <div className="mt-5">
                <Field label="Bitte beschreiben Sie kurz, welche Fenster schwer erreichbar sind">
                  <textarea
                    className={inputCls}
                    rows={3}
                    placeholder="z. B. Fenster im Treppenhaus, Dachfenster, Fenster im Obergeschoss, hinter Möbeln …"
                    value={form.accessibilityNote}
                    onChange={(e) => set('accessibilityNote', e.target.value)}
                    data-testid="window-access-note"
                  />
                </Field>
                <div className="mt-3">
                  <NoticeBox>
                    Bei schwer zugänglichen Fenstern prüfen wir den zusätzlichen Aufwand und erstellen gegebenenfalls
                    ein individuelles Angebot.
                  </NoticeBox>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div data-testid="window-step-5">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              In welchem Stockwerk befinden sich die Fenster?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Wählen Sie die passende Angabe.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {floorOptions.map((o) => (
                <OptionCard
                  key={o.value}
                  active={form.floor === o.value}
                  onClick={() => set('floor', o.value)}
                  title={o.title}
                  testid={`window-floor-${o.value}`}
                />
              ))}
            </div>
            {errors.floor && <p className="text-red-500 text-xs mt-2">{errors.floor}</p>}
            {(form.floor === 'hoeher' || form.floor === 'verschiedene') && (
              <div className="mt-5">
                <Field label="Weitere Informationen zur Zugänglichkeit (optional)">
                  <textarea
                    className={inputCls}
                    rows={3}
                    value={form.floorNote}
                    onChange={(e) => set('floorNote', e.target.value)}
                    data-testid="window-floor-note"
                  />
                </Field>
              </div>
            )}
          </div>
        )}

        {/* STEP 6 */}
        {step === 6 && (
          <div data-testid="window-step-6">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wie stark sind die Fenster verschmutzt?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Eine grobe Einschätzung genügt.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dirtOptions.map((o) => (
                <OptionCard
                  key={o.value}
                  active={form.dirt === o.value}
                  onClick={() => set('dirt', o.value)}
                  title={o.title}
                  testid={`window-dirt-${o.value}`}
                />
              ))}
            </div>
            {errors.dirt && <p className="text-red-500 text-xs mt-2">{errors.dirt}</p>}
            {(form.dirt === 'staerker' || form.dirt === 'sehr') && (
              <div className="mt-5">
                <NoticeBox>
                  Bei außergewöhnlich starker Verschmutzung kann je nach Aufwand ein zusätzlicher Preis entstehen. Wir
                  prüfen dies vor der endgültigen Auftragsbestätigung.
                </NoticeBox>
              </div>
            )}
          </div>
        )}

        {/* STEP 7 */}
        {step === 7 && (
          <div data-testid="window-step-7">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gibt es etwas, das wir beachten sollten?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Optionale Angaben zu Besonderheiten.</p>
            <Field label="Weitere Informationen / Besonderheiten">
              <textarea
                className={inputCls}
                rows={5}
                placeholder="z. B. Dachfenster vorhanden, Wintergarten, besondere Verschmutzung, Fenster schwer erreichbar, besondere Fensterarten, sonstige Wünsche"
                value={form.extras}
                onChange={(e) => set('extras', e.target.value)}
                data-testid="window-extras"
              />
            </Field>
          </div>
        )}

        {/* STEP 8 */}
        {step === 8 && (
          <div data-testid="window-step-8">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wie oft möchten Sie Ihre Fenster reinigen lassen?
            </h3>
            <p className="text-slate-500 text-sm mb-6">Einmalig oder regelmäßig – ganz nach Ihrem Bedarf.</p>
            <div className="space-y-3">
              {intervalOptions.map((o) => (
                <OptionCard
                  key={o.value}
                  active={form.interval === o.value}
                  onClick={() => set('interval', o.value)}
                  title={o.title}
                  desc={o.desc}
                  testid={`window-interval-${o.value}`}
                />
              ))}
            </div>
            {errors.interval && <p className="text-red-500 text-xs mt-2">{errors.interval}</p>}
            {form.interval === 'individuell' && (
              <div className="mt-5">
                <Field label="Wie häufig wünschen Sie die Fensterreinigung?" required error={errors.intervalCustom}>
                  <input
                    className={inputCls}
                    placeholder="z. B. monatlich, alle 3 Monate …"
                    value={form.intervalCustom}
                    onChange={(e) => set('intervalCustom', e.target.value)}
                    data-testid="window-interval-custom"
                  />
                </Field>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-5">
              Sie fragen das gewünschte Intervall an – es entsteht kein automatisch verbindlicher Vertrag und kein
              kostenpflichtiges Abo. Wir bestätigen anschließend das konkrete Angebot und die Termine.
            </p>
          </div>
        )}

        {/* REVIEW */}
        {isReview && (
          <div data-testid="window-review">
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bitte überprüfen Sie Ihre Angaben
            </h3>
            <p className="text-slate-500 text-sm mb-6">Alles korrekt? Dann senden Sie uns Ihre unverbindliche Anfrage.</p>

            <div className="space-y-2 text-sm bg-slate-50 rounded-2xl p-5 border border-slate-100">
              <SummaryRow label="Name" value={`${form.firstName} ${form.lastName}`} />
              <SummaryRow label="Adresse" value={`${form.street} ${form.houseNumber}, ${form.zip} ${form.city}`} />
              <SummaryRow label="Telefon" value={form.phone} />
              <SummaryRow label="E-Mail" value={form.email} />
              <SummaryRow label="Anzahl Fenster" value={form.windowCount} />
              <SummaryRow label="Reinigungsumfang" value="Glas innen, Glas außen, normaler Fensterrahmen" />
              <SummaryRow label="Stockwerk" value={labelMaps.floor[form.floor]} />
              <SummaryRow label="Zugänglichkeit" value={labelMaps.accessibility[form.accessibility]} />
              {form.accessibilityNote && <SummaryRow label="Hinweis Zugänglichkeit" value={form.accessibilityNote} />}
              <SummaryRow label="Verschmutzungsgrad" value={labelMaps.dirt[form.dirt]} />
              <SummaryRow
                label="Intervall"
                value={
                  form.interval === 'individuell'
                    ? `Individuell – ${form.intervalCustom}`
                    : labelMaps.interval[form.interval]
                }
              />
              {form.extras && <SummaryRow label="Besonderheiten" value={form.extras} />}
              <div className="flex justify-between pt-3 mt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-900">Preisorientierung</span>
                <span className="font-bold text-[#0052CC]" data-testid="window-review-price">
                  <span className="text-xs uppercase">ab</span> {price} €
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              Die angezeigte Summe ist eine unverbindliche Preisorientierung. Bei besonderen Gegebenheiten,
              außergewöhnlicher Verschmutzung, besonderen Fensterarten oder schwer zugänglichen Fenstern kann der
              tatsächliche Preis abweichen. Das endgültige Angebot bestätigen wir nach Prüfung Ihrer Angaben.
            </p>

            <div className="space-y-3 mt-6">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="window-confirm-data">
                <input
                  type="checkbox"
                  checked={confirmData}
                  onChange={(e) => {
                    setConfirmData(e.target.checked);
                    setErrors((p) => ({ ...p, confirmData: undefined }));
                  }}
                  className="mt-1 w-5 h-5 accent-[#0052CC]"
                />
                <span className={`text-sm ${errors.confirmData ? 'text-red-500' : 'text-slate-600'}`}>
                  Ich habe meine Angaben überprüft und möchte eine unverbindliche Angebotsanfrage senden.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="window-confirm-privacy">
                <input
                  type="checkbox"
                  checked={confirmPrivacy}
                  onChange={(e) => {
                    setConfirmPrivacy(e.target.checked);
                    setErrors((p) => ({ ...p, confirmPrivacy: undefined }));
                  }}
                  className="mt-1 w-5 h-5 accent-[#0052CC]"
                />
                <span className={`text-sm ${errors.confirmPrivacy ? 'text-red-500' : 'text-slate-600'}`}>
                  Ich habe die{' '}
                  <Link to="/datenschutz" className="text-[#0052CC] underline" target="_blank">
                    Datenschutzerklärung
                  </Link>{' '}
                  gelesen.
                </span>
              </label>
            </div>

            {submitError && <p className="text-red-500 text-sm mt-4" data-testid="window-submit-error">{submitError}</p>}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-slate-100">
          {step > 1 ? (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium text-slate-600 hover:bg-slate-100 transition"
              data-testid="window-back"
            >
              <ArrowLeft className="w-5 h-5" /> Zurück
            </button>
          ) : (
            <span />
          )}

          {!isReview ? (
            <button
              type="button"
              onClick={next}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="window-next"
            >
              {step === TOTAL_STEPS ? 'Zur Zusammenfassung' : 'Weiter'} <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="window-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              Unverbindliches Angebot anfragen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium text-right">{value || '–'}</span>
  </div>
);

const benefits = [
  { icon: Sparkles, title: 'Ab 10 € pro Fenster', text: 'Transparente Preisorientierung für die Standard-Fensterreinigung.' },
  { icon: Droplets, title: 'Innen & außen', text: 'Glasscheiben werden innen und außen gereinigt.' },
  { icon: CheckCircle2, title: 'Rahmen inklusive', text: 'Der normale Fensterrahmen ist im Grundpreis enthalten.' },
  { icon: Repeat, title: 'Flexibel', text: 'Einmalige oder regelmäßige Fensterreinigung – nach individuellem Bedarf.' },
  { icon: ArrowRight, title: 'Einfach online anfragen', text: 'Fensteranzahl, Intervall und Besonderheiten online auswählen und Anfrage abschicken.' },
  { icon: ShieldCheck, title: 'Persönlich', text: 'Bei besonderen Anforderungen prüfen wir den Aufwand und erstellen ggf. ein individuelles Angebot.' },
];

const FensterreinigungPage = () => {
  const configuratorRef = useRef(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Fensterreinigung Oldenburg | Ab 10 € pro Fenster – BunterKopf';
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (metaDesc)
      metaDesc.setAttribute(
        'content',
        'Professionelle Fensterreinigung in Oldenburg und Umgebung – ab 10 € pro Fenster. Glas innen & außen inkl. Rahmen. Jetzt online konfigurieren und unverbindliches Angebot anfragen.'
      );
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'window-cleaning-ld';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Fensterreinigung',
      provider: { '@type': 'CleaningService', name: 'BunterKopf Gebäudereinigung', telephone: '+49 441 93925989' },
      areaServed: { '@type': 'City', name: 'Oldenburg' },
      offers: { '@type': 'Offer', price: '10', priceCurrency: 'EUR', description: 'Fensterreinigung ab 10 € pro Fenster' },
      name: 'Fensterreinigung Oldenburg',
    });
    document.head.appendChild(ld);
    if (window.location.hash === '#konfigurator') {
      setTimeout(() => {
        if (configuratorRef.current) configuratorRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute('content', prevDesc);
      const el = document.getElementById('window-cleaning-ld');
      if (el) el.remove();
    };
  }, []);

  const scrollToConfigurator = () => {
    if (configuratorRef.current) configuratorRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div data-testid="fensterreinigung-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#F5F9FF] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionBadge>Fensterreinigung in Oldenburg</SectionBadge>
          <h1
            className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-5"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Professionelle Fensterreinigung in Oldenburg
          </h1>
          <p className="text-xl text-[#0052CC] font-medium mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Saubere Fenster. Klare Sicht. Kein Aufwand für Sie.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            Wir reinigen Ihre Fenster zuverlässig und gründlich – einmalig oder regelmäßig, ganz nach Ihrem Bedarf.
          </p>

          <div className="inline-flex items-baseline gap-2 bg-white border border-[#BFDBFE] shadow-sm rounded-2xl px-6 py-4 mb-8">
            <span className="text-sm font-bold uppercase tracking-wide text-white bg-[#0052CC] px-2.5 py-1 rounded-lg">
              ab
            </span>
            <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              10 €
            </span>
            <span className="text-slate-600">pro Fenster</span>
          </div>

          <div>
            <button
              type="button"
              onClick={scrollToConfigurator}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="hero-configure-button"
            >
              Fensterreinigung konfigurieren <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Was ist im Preis enthalten */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <SectionBadge>Transparent & fair</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Was ist im Preis enthalten?
            </h2>
            <p className="text-slate-600 mt-3">Der Grundpreis von ab 10 € pro Fenster beinhaltet:</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              'Reinigung der Glasscheibe von innen',
              'Reinigung der Glasscheibe von außen',
              'Reinigung des normalen Fensterrahmens',
              'Gründliche Reinigung der zugänglichen Fensterflächen',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl p-4">
                <CheckCircle2 className="w-5 h-5 text-[#00C2B8] flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto mt-6">
            <NoticeBox>
              Der äußere Falz bzw. tieferliegende Bereiche des Fensterrahmens sind nicht automatisch Bestandteil der
              Standardreinigung. Falls bestimmte Fenster aufgrund ihrer Bauart, Zugänglichkeit oder eines
              außergewöhnlichen Reinigungsaufwands nicht zum Standardpreis gereinigt werden können, erstellen wir
              Ihnen gerne ein individuelles Angebot.
            </NoticeBox>
          </div>
        </div>
      </section>

      {/* Einmalig oder regelmäßig */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <SectionBadge>Ganz nach Ihrem Rhythmus</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wie oft möchten Sie Ihre Fenster reinigen lassen?
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Ob einmalig oder regelmäßig – Sie fragen Ihr Wunsch-Intervall an, wir bestätigen anschließend Angebot und
              Termine. Kein automatisches Abo.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {intervalOptions.map((o) => (
              <div key={o.value} className="bg-white border border-slate-100 rounded-2xl p-6">
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {o.title}
                </h3>
                <p className="text-sm text-slate-500">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SectionBadge>Ihre Vorteile</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Warum BunterKopf für Ihre Fenster?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-100" data-testid={`window-benefit-${i}`}>
                <div className="w-12 h-12 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-4">
                  <b.icon className="w-6 h-6 text-[#0052CC]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {b.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Konfigurator */}
      <section ref={configuratorRef} id="konfigurator" className="py-16 md:py-24 bg-slate-50 scroll-mt-24" data-testid="window-configurator">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SectionBadge>Fensterreinigungs-Konfigurator</SectionBadge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Konfigurieren Sie Ihre Fensterreinigung
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              In wenigen Schritten zur unverbindlichen Angebotsanfrage – einfach und schnell.
            </p>
          </div>
          <WindowConfigurator />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#0052CC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bereit für klare Sicht?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Konfigurieren Sie jetzt Ihre Fensterreinigung und senden Sie uns Ihre unverbindliche Anfrage.
          </p>
          <button
            type="button"
            onClick={scrollToConfigurator}
            className="inline-flex items-center justify-center gap-2 bg-white text-[#0052CC] px-8 py-4 rounded-full font-medium hover:bg-blue-50 transition-colors duration-300 shadow-lg"
            data-testid="final-cta-configure"
          >
            Fensterreinigung anfragen <ArrowRight className="w-5 h-5" />
          </button>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-blue-100 text-sm">
            <a href="tel:044193925989" className="inline-flex items-center gap-2 hover:text-white transition">
              <Phone className="w-4 h-4" /> 0441 93925989
            </a>
            <a href="mailto:service@bunterkopf-reinigung.de" className="inline-flex items-center gap-2 hover:text-white transition">
              <Mail className="w-4 h-4" /> service@bunterkopf-reinigung.de
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FensterreinigungPage;
